import { Fragment, Suspense, lazy, useEffect } from "react";
import type { ComponentType, ReactNode } from "react";
import { Link, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";

import {
  ErrorBoundary,
  GlobalImport,
  UserPrefsProvider,
  WorkbenchHostActions,
  usePreferences,
} from "@strat/ui";
import type { WorkbenchHostContext } from "@strat/ui";

type LabName =
  | "BenchmarkPage"
  | "DocsPage"
  | "HistoryPage"
  | "OptimizePage"
  | "RobustnessPage"
  | "StrategiesPage"
  | "WalkForwardPage"
  | "WorkbenchPage";
const lab = (name: LabName) =>
  lazy(() => import("./labPages").then((m) => ({ default: m[name] as ComponentType })));
const WorkbenchPage = lab("WorkbenchPage");
const StrategiesPage = lab("StrategiesPage");
const HistoryPage = lab("HistoryPage");
const OptimizePage = lab("OptimizePage");
const WalkForwardPage = lab("WalkForwardPage");
const RobustnessPage = lab("RobustnessPage");
const LabBenchmarkPage = lab("BenchmarkPage");
const DocsPage = lab("DocsPage");
const RunAsBotButton = lazy(() =>
  import("./components/RunAsBotButton").then((m) => ({ default: m.RunAsBotButton })),
);

/** The hub's action in the Workbench's host slot: run the rules as a paper bot. */
function workbenchActions(ctx: WorkbenchHostContext): ReactNode {
  return (
    <Suspense fallback={null}>
      <RunAsBotButton ctx={ctx} />
    </Suspense>
  );
}

import type { Role } from "./api";
import { SavedBacktests } from "./components/SavedBacktests";
import { AdminPage } from "./pages/admin/AdminPage";
import { DownloadPage } from "./pages/Download";
import { HomePage } from "./pages/Home";
import { MySpacePage } from "./pages/MySpace";
import { ProfilePage } from "./pages/Profile";
import { SettingsPage } from "./pages/Settings";
import { SignInPage } from "./pages/SignIn";
import { SignUpPage } from "./pages/SignUp";
import { BenchmarksPage } from "./pages/social/Benchmarks";
import { BotPage, BotsExplorePage, MyBotsPage } from "./pages/social/bots";
import { ExplorePage } from "./pages/social/Explore";
import { MarketDataPage } from "./pages/social/MarketData";
import { NotificationsPage } from "./pages/social/Notifications";
import { LeaguePage, SeasonPage } from "./pages/social/League";
import { RefStrategyPage } from "./pages/social/RefStrategyPage";
import { RunPage } from "./pages/social/RunPage";
import { StrategyPage } from "./pages/social/StrategyPage";
import { useSession } from "./session";
import { Guard } from "./shell/Guard";
import { MySpaceTabs } from "./shell/MySpaceTabs";
import { Header } from "./shell/Header";
import { Card, EmptyState } from "./shell/ui";

function NotFound() {
  return (
    <Card>
      <EmptyState
        icon="◇"
        title="No such page"
        action={
          <Link className="btn btn--primary" to="/">
            Go home
          </Link>
        }
      >
        That address doesn't match anything on StrategyHive.
      </EmptyState>
    </Card>
  );
}

function Footer() {
  return (
    <footer className="footer muted small">
      StrategyHive · paper trading only · nothing here is investment advice · simulated results do not predict
      future returns · <Link to="/download">Get the app</Link> · <Link to="/about">About</Link>
    </footer>
  );
}

/**
 * The lab's pages decide how much to show from the Starter/Pro preference;
 * on the hub that follows the member's layout, so the two can never disagree.
 */
function LayoutSync() {
  const { user } = useSession();
  const { prefs, update } = usePreferences();
  const want = user && user.role !== "starter" ? "pro" : "starter";
  useEffect(() => {
    if (prefs.experience !== want) update({ experience: want });
  }, [want, prefs.experience, update]);
  return null;
}

/** A lab page, behind the role it needs, inside its own error boundary. */
function Lab({ role, children }: { role: Role; children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <Guard role={role}>
      <ErrorBoundary key={pathname.split("/")[1] ?? ""}>
        <Suspense fallback={<p className="muted">Loading…</p>}>{children}</Suspense>
      </ErrorBoundary>
    </Guard>
  );
}

/**
 * A detail page, remounted when its id changes. Sections are no longer
 * remounted on every URL change (see the ErrorBoundary keys below), so going
 * from one strategy to another must not carry the first one's local state —
 * a verify verdict, an open dialog — onto the second.
 */
function ById({ children }: { children: ReactNode }) {
  const { id, username, namespace, ref } = useParams();
  return <Fragment key={id ?? username ?? `${namespace}/${ref}`}>{children}</Fragment>;
}

function AboutPage() {
  return (
    <Card title="About StrategyHive">
      <div className="stack prose">
        <p>
          StrategyHive is a place to build trading strategies without code, test them on real market history,
          run them as paper bots, and share what you find.
        </p>
        <p>
          <strong>Everything is paper.</strong> No money is traded, no exchange keys are ever asked for, and
          nothing here is investment advice. Backtests and paper bots describe the past; they do not predict
          the future.
        </p>
        <p>
          <strong>Check the numbers yourself.</strong> Every published backtest can be re-run on
          StrategyHive's engine and compared trade by trade, and every hosted bot's trades are chained into a
          ledger that cannot be edited without it showing.
        </p>
      </div>
    </Card>
  );
}

/** Dropping a file on the hub imports it into your library — so only when signed in. */
function SignedInImport() {
  const { user } = useSession();
  return <GlobalImport enabled={!!user} />;
}

export function App() {
  const { pathname } = useLocation();
  const { user } = useSession();
  return (
    // Signed in, preferences follow the account; a visitor keeps them in this browser.
    <UserPrefsProvider account={user?.id ?? null}>
      <div className="app">
        <LayoutSync />
        <SignedInImport />
        <Header />
        <main className="app__main">
          <ErrorBoundary key={pathname.split("/")[1] ?? ""}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/download" element={<DownloadPage />} />
              <Route path="/signin" element={<SignInPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/me" element={<MySpacePage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route
                path="/u/:username"
                element={
                  <ById>
                    <ProfilePage />
                  </ById>
                }
              />
              <Route path="/admin" element={<AdminPage />} />

              {/* Public, readable by guests. */}
              <Route path="/explore" element={<ExplorePage />} />
              <Route
                path="/s/:id"
                element={
                  <ById>
                    <StrategyPage />
                  </ById>
                }
              />
              {/* A reference: /s/alex/rsi-dip:v2 or /s/alex/rsi-dip@sha256:… */}
              <Route
                path="/s/:namespace/:ref"
                element={
                  <ById>
                    <RefStrategyPage />
                  </ById>
                }
              />
              <Route
                path="/r/:id"
                element={
                  <ById>
                    <RunPage />
                  </ById>
                }
              />
              <Route
                path="/b/:id"
                element={
                  <ById>
                    <BotPage />
                  </ById>
                }
              />
              <Route path="/explore/bots" element={<BotsExplorePage />} />
              <Route path="/leaderboard" element={<Navigate to="/explore/bots" replace />} />
              <Route path="/benchmarks" element={<BenchmarksPage />} />
              <Route path="/league" element={<LeaguePage />} />
              <Route
                path="/league/:id"
                element={
                  <ById>
                    <SeasonPage />
                  </ById>
                }
              />
              <Route
                path="/docs"
                element={
                  <Suspense fallback={null}>
                    <DocsPage />
                  </Suspense>
                }
              />
              <Route
                path="/docs/:chapterId"
                element={
                  <Suspense fallback={null}>
                    <DocsPage />
                  </Suspense>
                }
              />

              {/* StrategyLab's own pages, unchanged, on the hub's data. */}
              <Route
                path="/workbench"
                element={
                  <Lab role="starter">
                    <WorkbenchHostActions.Provider value={workbenchActions}>
                      <WorkbenchPage />
                    </WorkbenchHostActions.Provider>
                  </Lab>
                }
              />
              <Route
                path="/workbench/:strategyId"
                element={
                  <Lab role="starter">
                    <WorkbenchHostActions.Provider value={workbenchActions}>
                      <WorkbenchPage />
                    </WorkbenchHostActions.Provider>
                  </Lab>
                }
              />
              <Route
                path="/strategies"
                element={
                  <Lab role="starter">
                    <MySpaceTabs />
                    <StrategiesPage />
                  </Lab>
                }
              />
              <Route
                path="/history"
                element={
                  <Lab role="starter">
                    <MySpaceTabs />
                    <div className="stack">
                      <HistoryPage />
                      <SavedBacktests />
                    </div>
                  </Lab>
                }
              />
              <Route
                path="/optimize"
                element={
                  <Lab role="pro">
                    <OptimizePage />
                  </Lab>
                }
              />
              <Route
                path="/walkforward"
                element={
                  <Lab role="pro">
                    <WalkForwardPage />
                  </Lab>
                }
              />
              <Route
                path="/robustness"
                element={
                  <Lab role="pro">
                    <RobustnessPage />
                  </Lab>
                }
              />
              <Route
                path="/benchmark"
                element={
                  <Lab role="pro">
                    <LabBenchmarkPage />
                  </Lab>
                }
              />
              <Route path="/market-data" element={<MarketDataPage />} />
              <Route
                path="/bots"
                element={
                  <>
                    <MySpaceTabs />
                    <MyBotsPage />
                  </>
                }
              />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </ErrorBoundary>
        </main>
        <Footer />
      </div>
    </UserPrefsProvider>
  );
}
